import { useCallback } from 'react'

import { create } from '@bufbuild/protobuf'
import { createClient } from '@connectrpc/connect'
import { useTransport } from '@connectrpc/connect-query'
import {
  type SplitSupplyNoticeDownloadFormat,
  type SplitSupplyNoticeKey,
} from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'
import {
  DownloadOwnSplitSupplyNoticeXmlRequestSchema,
  SplitSupplyPlanService,
} from '@ebalance/proto/bffuser/v1/split_supply_plan_service_pb'
import { useTranslation } from 'react-i18next'

import { useErrorToast } from '~/hooks/useErrorToast'
import { downloadBytes } from '~/libs/downloadBytes'
import { describeSplitSupplyNoticeError } from '~/pages/SplitSupplyPlan/models/serverError'

/**
 * 自社の通告値の XML / ZIP ダウンロード hook。
 *
 * `SplitSupplyPlanService.DownloadOwnSplitSupplyNoticeXml` を押下時に命令的に呼び、
 * レスポンスの content を Blob 経由でブラウザダウンロードする。 ファイル名は必ずレスポンスの
 * fileName (広域機関のファイル名) を使い、 frontend で組み立てない。 形式は
 * 「自動」 (100 件以下は XML、 超えると ZIP) / XML / ZIP から呼び出し側が選ぶ。
 */
export function useDownloadOwnSplitSupplyNoticeXml(): {
  download: (
    key: SplitSupplyNoticeKey,
    format: SplitSupplyNoticeDownloadFormat,
  ) => Promise<void>
} {
  const { t } = useTranslation()
  const transport = useTransport()
  const showErrorToast = useErrorToast()

  const download = useCallback(
    async (
      key: SplitSupplyNoticeKey,
      format: SplitSupplyNoticeDownloadFormat,
    ): Promise<void> => {
      const client = createClient(SplitSupplyPlanService, transport)
      try {
        const res = await client.downloadOwnSplitSupplyNoticeXml(
          create(DownloadOwnSplitSupplyNoticeXmlRequestSchema, { key, format }),
        )
        // ファイル名はレスポンスの fileName を使う (frontend で名前を組み立てない)。
        downloadBytes(res.content, res.contentType, res.fileName)
      } catch (err) {
        showErrorToast({
          title: t('page:splitSupplyPlan.download.errorTitle'),
          description: describeSplitSupplyNoticeError(err, t),
        })
      }
    },
    [transport, t, showErrorToast],
  )

  return { download }
}
